import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import DashboardLayout from "../../layouts/DashboardLayout";
import StoreBreadcrumbs from "./components/StoreBreadcrumbs";
import DashboardSummaryStrip from "../../components/DashboardSummaryStrip/DashboardSummaryStrip";
import localeText from "../../../../locale";
import { formatDate } from "../../../../utilities/formatDate";
import { formatPrice } from "../../../../utilities/formatPrice";
import useOrders from "../../components/Orders/hooks/useOrders";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import "../FinancialHub/components/FinancialTransactionsTable/FinancialTransactionsTable.css";
import "./StoreOrdersPage.css";

const STATUS_OPTIONS = [
  { label: "—", value: null },
  { label: "PAID", value: "PAID" },
  { label: "SENT", value: "SENT" },
  { label: "RECEIVED", value: "RECEIVED" },
];

const StoreOrdersPage = () => {
  const history = useHistory();
  const { showErrorToast } = useToastContext();
  const [filterOrderId, setFilterOrderId] = useState("");
  const [filterDateRange, setFilterDateRange] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);

  const { isLoading: loading, orders: rawOrders } = useOrders({
    sales: true,
    onErrorLoadUserOrders: (code) => showErrorToast({ code }),
  });

  const orders = useMemo(() => {
    let list = Array.isArray(rawOrders) ? rawOrders : [];
    if (filterOrderId?.trim()) {
      const q = filterOrderId.trim().toLowerCase();
      list = list.filter((o) =>
        String(o.id || "")
          .toLowerCase()
          .includes(q),
      );
    }
    if (filterDateRange?.[0] && filterDateRange[1]) {
      const start = new Date(filterDateRange[0]).getTime();
      const end = new Date(filterDateRange[1]).getTime();
      list = list.filter((o) => {
        const t = new Date(o.date).getTime();
        return t >= start && t <= end;
      });
    }
    if (filterStatus) {
      list = list.filter((o) => o.status === filterStatus);
    }
    return list
      .map((o) => ({
        ...o,
        formatedDate: formatDate(new Date(o.date)),
        dateValue: o.date ? new Date(o.date).getTime() : 0,
        firstProductName:
          o.itens?.[0]?.product?.title ?? o.itens?.[0]?.product?.description ?? "—",
        totalValue: Number(o.amountTotal ?? o.amountOrder ?? 0) || 0,
      }))
      .sort((a, b) => b.dateValue - a.dateValue);
  }, [rawOrders, filterOrderId, filterDateRange, filterStatus]);

  const kpis = useMemo(() => {
    const list = Array.isArray(rawOrders) ? rawOrders : [];
    const totalOrders = list.length;
    const totalRevenue = list
      .filter((o) => o.status === "RECEIVED")
      .reduce((acc, o) => acc + (Number(o.amountTotal ?? o.amountOrder ?? 0) || 0), 0);
    const pendingOrders = list.filter((o) => o.status === "PAID" || o.status === "SENT").length;
    return { totalOrders, totalRevenue, pendingOrders };
  }, [rawOrders]);

  const getStatusLabel = (status) => {
    if (status === "SENT") return localeText("sent") || "Sent";
    if (status === "PAID") return localeText("paid") || "Paid";
    if (status === "RECEIVED") return localeText("received") || "Received";
    return status || "—";
  };

  const statusConfig = {
    RECEIVED: { color: "var(--theme-primary, #1DAF61)" },
    SENT: { color: "#42A5F5" },
    PAID: { color: "#F7B005" },
    CANCELED: { color: "#eb4034" },
  };

  return (
    <DashboardLayout>
      <StoreBreadcrumbs />
      <div className="flex align-items-center gap-4 mb-5 mt-4">
        <div
          className="border-circle p-3"
          style={{
            border: "1px solid var(--theme-border, #EBEBEB)",
            width: "54px",
            height: "54px",
          }}
        >
          <i
            className="pi pi-clock text-xl"
            style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
          />
        </div>
        <div>
          <h2 className="text-lg font-semibold m-0" style={{ color: "var(--theme-text)" }}>
            {localeText("storeOrders")}
          </h2>
          <p className="text-sm m-0 mt-1" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
            {localeText("storePanelSubtitle")}
          </p>
        </div>
      </div>

      <DashboardSummaryStrip
        items={[
          {
            label: localeText("storeTotalOrders") || "Total de pedidos",
            value: kpis.totalOrders,
          },
          {
            label: localeText("storeRevenue"),
            value: <>€ {formatPrice(kpis.totalRevenue)}</>,
          },
          {
            label: localeText("storePendingOrders") || "Pedidos pendentes",
            value: kpis.pendingOrders,
            valueColor: "var(--theme-primary)",
          },
        ]}
        className="store-panel-summary"
      />

      <div className="flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
        <div className="flex gap-3">
          {" "}
          <InputText
            value={filterOrderId}
            onChange={(e) => setFilterOrderId(e.target.value)}
            placeholder={localeText("filterByOrderId")}
            className="p-inputtext-sm border-round-md p-no-outline"
          />
          <Calendar
            value={filterDateRange}
            onChange={(e) => setFilterDateRange(e.value)}
            selectionMode="range"
            readOnlyInput
            placeholder={localeText("filterByDateRange")}
            dateFormat="dd/mm/yy"
            className="p-inputtext-sm border-round-md p-no-outline"
          />
        </div>

        <Dropdown
          value={filterStatus}
          options={STATUS_OPTIONS}
          onChange={(e) => setFilterStatus(e.value)}
          placeholder={localeText("filterByStatus")}
          className="p-inputtext-sm border-round-md p-no-outline"
          style={{ minWidth: "10rem" }}
        />
      </div>

      <div className="financial-transactions-table">
        <DataTable
          value={orders}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 20, 50]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
          currentPageReportTemplate=""
          sortField="dateValue"
          sortOrder={-1}
          emptyMessage={
            <div className="flex flex-column align-items-center justify-content-center py-8 gap-3">
              <div
                className="border-round flex align-items-center justify-content-center"
                style={{
                  width: "120px",
                  height: "120px",
                  backgroundColor: "color-mix(in srgb, var(--theme-border) 35%, transparent)",
                }}
              >
                <i className="pi pi-inbox text-6xl text-400" />
              </div>
              <p className="m-0" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
                {localeText("noOperationsRecords")}
              </p>
            </div>
          }
          onRowClick={(e) => history.push(`/dashboard-store/orders/details?orderId=${e.data.id}`)}
          // PrimeReact 10 passa [rowData, options] como array para o rowClassName
          rowClassName={(args) =>
            (Array.isArray(args) ? args[0] : args)?.status === "PAID"
              ? "cursor-pointer store-order-row--pending-action"
              : "cursor-pointer"
          }
          className="financial-transactions-datatable"
        >
          <Column
            field="id"
            header="ID"
            sortable
            body={(row) => <span className="font-medium">{row.id}</span>}
          />
          <Column
            field="formatedDate"
            sortField="dateValue"
            header={localeText("date")}
            sortable
            body={(row) => row.formatedDate}
          />
          <Column
            field="status"
            header={localeText("status")}
            body={(row) => {
              const cfg = statusConfig[row.status] || statusConfig.PAID;
              return (
                <span className="flex align-items-center gap-2">
                  <span
                    className="border-circle"
                    style={{ width: "8px", height: "8px", backgroundColor: cfg.color }}
                  />
                  {getStatusLabel(row.status)}
                </span>
              );
            }}
          />
          <Column
            header={localeText("customer")}
            body={(row) => row.buyer?.username ?? row.buyer?.firstname ?? "—"}
          />
          <Column header={localeText("product")} body={(row) => row.firstProductName} />
          <Column
            header={localeText("value")}
            body={(row) => <span className="font-medium">€ {formatPrice(row.totalValue)}</span>}
            sortable
            field="totalValue"
          />
          <Column
            header=""
            body={() => <i className="pi pi-chevron-right text-400" />}
            style={{ width: "40px" }}
          />
        </DataTable>
      </div>
    </DashboardLayout>
  );
};

export default StoreOrdersPage;
